/**
 * Contexto de Smooth Scroll
 * Provee scroll suave global usando Lenis para una experiencia inmersiva tipo Awwwards
 * 
 * Este contexto implementa:
 * - Scroll suave con inercia física
 * - Integración con GSAP ScrollTrigger
 * - Control del scroll desde cualquier componente
 */

import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import Lenis from 'lenis';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Registrar plugins de GSAP
gsap.registerPlugin(ScrollTrigger);

/**
 * Contexto para el sistema de scroll suave
 * @type {React.Context}
 */
const SmoothScrollContext = createContext({
  lenis: null,
  scrollTo: () => {},
  scrollProgress: 0,
  isScrolling: false,
});

/**
 * Hook personalizado para acceder al contexto de scroll
 * @returns {Object} Contexto de scroll con lenis, scrollTo, scrollProgress e isScrolling
 */
export const useSmoothScroll = () => {
  const context = useContext(SmoothScrollContext);
  if (!context) {
    throw new Error('useSmoothScroll debe usarse dentro de SmoothScrollProvider');
  }
  return context;
};

/**
 * Proveedor del sistema de scroll suave
 * Inicializa Lenis y lo sincroniza con GSAP ScrollTrigger
 * 
 * @param {Object} props - Propiedades del componente
 * @param {React.ReactNode} props.children - Componentes hijos
 * @returns {JSX.Element} Proveedor del contexto
 */
export const SmoothScrollProvider = ({ children }) => {
  const lenisRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);
  const location = useLocation();

  // Inicialización de Lenis
  useEffect(() => {
    // Inicializar Lenis con configuración premium
    const lenis = new Lenis({
      duration: 1.2, // Duración del scroll suave
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), // Easing exponencial suave
      direction: 'vertical',
      gestureDirection: 'vertical',
      smooth: true,
      smoothTouch: false, // Desactivar en táctil para mejor rendimiento
      touchMultiplier: 2,
      infinite: false,
    });

    lenisRef.current = lenis;
    
    // Exponer Lenis globalmente para ScrollToTop
    window.__lenis = lenis;
    
    // Scroll al inicio inmediatamente después de inicializar Lenis
    lenis.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);

    // Actualizar ScrollTrigger en cada frame de Lenis
    lenis.on('scroll', ScrollTrigger.update);

    // Escuchar eventos de scroll para actualizar el estado
    lenis.on('scroll', ({ progress, velocity }) => {
      setScrollProgress(progress);
      setIsScrolling(Math.abs(velocity) > 0.01);
    });

    // Sincronizar Lenis con el ticker de GSAP para rendimiento óptimo
    gsap.ticker.add((time) => {
      lenis.raf(time * 1000);
    });

    // Configurar ScrollTrigger para usar Lenis
    gsap.ticker.lagSmoothing(0);

    // Limpiar al desmontar
    return () => {
      lenis.destroy();
      gsap.ticker.remove(lenis.raf);
      window.__lenis = null;
    };
  }, []);

  // Scroll al inicio cuando cambia la ruta
  useEffect(() => {
    // Usar requestAnimationFrame para asegurar que Lenis esté listo
    const scrollToTop = () => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      
      if (lenisRef.current) {
        lenisRef.current.scrollTo(0, { immediate: true });
      }
    };
    
    // Ejecutar inmediatamente
    scrollToTop();
    
    // Reforzar después de un frame
    requestAnimationFrame(() => {
      scrollToTop();
    });
    
    // Y otra vez con delay para asegurar
    const timer = setTimeout(scrollToTop, 50);
    
    return () => clearTimeout(timer);
  }, [location.pathname, location.key]);

  /**
   * Función para hacer scroll a un elemento o posición
   * @param {string|number|HTMLElement} target - Destino del scroll
   * @param {Object} options - Opciones adicionales de scroll
   */
  const scrollTo = (target, options = {}) => {
    if (lenisRef.current) {
      lenisRef.current.scrollTo(target, {
        duration: 1.2,
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        ...options,
      });
    }
  };

  const value = {
    lenis: lenisRef.current,
    scrollTo,
    scrollProgress,
    isScrolling,
  };

  return (
    <SmoothScrollContext.Provider value={value}>
      {children}
    </SmoothScrollContext.Provider>
  );
};

SmoothScrollProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default SmoothScrollContext;
